// Estado de impersonación "ver como alumno" (F3 RBAC · read-only) en el front.
// ─────────────────────────────────────────────────────────────────────────
// Contrato backend (admin_impersonation.py):
//  - POST /api/admin/users/{id}/impersonate → { token, token_type:'Bearer',
//    impersonating:true, actor:{id,email}, target:{id,email,role},
//    expires_at:<unix>, jti, readonly:true }
//  - El `token` es un JWT PROPIO del campus (no el de Supabase del alumno) que
//    se usa como Bearer en TODAS las llamadas siguientes; mientras esté activo
//    el backend resuelve la sesión como el ALUMNO objetivo y BLOQUEA escrituras
//    (403 en POST/PUT/PATCH/DELETE).
//  - POST /api/admin/impersonate/stop { token } → cierra el rastro de auditoría.
//    "Terminar" = el front descarta el token (este módulo). El token expira solo
//    a los 15 min aunque no se llame a stop.
//
// PERSISTENCIA: sessionStorage (no localStorage) → la impersonación sobrevive a
// la navegación y a un refresh, pero NO al cierre de la pestaña (defensa: un
// token read-only de 15 min no debe quedar latente entre sesiones de navegador).

import type { AuthIdentity } from './types';

const IMPERSONATION_KEY = 'robotea-impersonation';

/** Sesión de impersonación activa guardada en el front (subset del contrato). */
export interface ImpersonationSession {
  /** JWT de impersonación · se usa como Bearer en lib/api buildHeaders. */
  token: string;
  /** Email del alumno objetivo (lo que se "ve como"). */
  targetEmail: string;
  /** Email del admin real que inició la impersonación (para el banner). */
  actorEmail: string;
  /** Expiración (epoch segundos · claim exp del token). */
  expiresAt: number;
  /** jti · empareja start/stop en auditoría. */
  jti: string;
  /** Si la sesión es modo "alumno de prueba" (sintético), el tier elegido
   *  (start/basic/pro). El banner lo usa para mostrar una etiqueta amigable en
   *  vez del email sintético. undefined = impersonación de un alumno real. */
  demoTier?: string;
}

/** Respuesta cruda del endpoint /impersonate (lo que devuelve el backend). */
export interface ImpersonateStartResponse {
  token: string;
  token_type: string;
  impersonating: boolean;
  actor: { id: string; email: string };
  target: { id: string; email: string; role: string; tier?: string };
  expires_at: number;
  jti: string;
  readonly: boolean;
  /** true en el modo "alumno de prueba" (sintético). */
  demo?: boolean;
  /** tier del modo prueba (start/basic/pro). */
  tier?: string;
}

/** Lee la sesión de impersonación activa (o null). Descarta una expirada. */
export function getImpersonation(): ImpersonationSession | null {
  if (typeof sessionStorage === 'undefined') return null;
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(IMPERSONATION_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  let parsed: ImpersonationSession;
  try {
    parsed = JSON.parse(raw) as ImpersonationSession;
  } catch {
    clearImpersonation();
    return null;
  }
  // Validación defensiva del shape + expiración (margen 0: el backend rechaza
  // el token expirado igualmente, pero limpiamos el front para no enviar basura).
  if (
    typeof parsed.token !== 'string' ||
    typeof parsed.expiresAt !== 'number' ||
    typeof parsed.targetEmail !== 'string'
  ) {
    clearImpersonation();
    return null;
  }
  if (parsed.expiresAt * 1000 <= Date.now()) {
    clearImpersonation();
    return null;
  }
  return parsed;
}

/** Persiste una sesión de impersonación a partir de la respuesta del backend. */
export function setImpersonationFromResponse(res: ImpersonateStartResponse): ImpersonationSession {
  const session: ImpersonationSession = {
    token: res.token,
    targetEmail: res.target.email,
    actorEmail: res.actor.email,
    expiresAt: res.expires_at,
    jti: res.jti,
    demoTier: res.demo ? res.tier ?? res.target.tier : undefined,
  };
  try {
    sessionStorage.setItem(IMPERSONATION_KEY, JSON.stringify(session));
  } catch {
    /* sessionStorage no disponible · la impersonación no podrá persistir */
  }
  return session;
}

/** Borra la impersonación activa (al pulsar "Salir" o al expirar). */
export function clearImpersonation(): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(IMPERSONATION_KEY);
  } catch {
    /* noop */
  }
}

/** Token Bearer de impersonación activo (o null) · lo lee lib/api buildHeaders. */
export function getImpersonationToken(): string | null {
  return getImpersonation()?.token ?? null;
}

/** Bearer a usar: token de impersonación si existe (prioridad), si no el JWT real. */
export function authBearer(auth: AuthIdentity | null): string | null {
  const imp = getImpersonationToken();
  if (imp) return imp;
  if (auth && auth.mode === 'supabase' && auth.jwt) return auth.jwt;
  return null;
}
